import { DataGrid, DataGridPagination, Image, Loading, Modal, UnicFilterBar } from 'src/components';
import { useDispatch, useSelector } from 'react-redux';
import { Creators as CreatorsVariations } from 'src/store/ducks/products_variations';
import { Box, Button, Checkbox, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';
import { Lock, AppsOutage } from '@mui/icons-material';
import { getErrors, getRequestOperator, getScreenSize, simpleAddress } from 'src/utils/getUtils';
import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import FlatIcon, { FlatIconOptions } from 'src/components/ui/FlatIcon';

export default function AddProductsModal({ open, onClose, options }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [selp, setSelp] = useState([]);
  const tableRef = useRef(null);

  const locks = {
    allocation: options?.locks?.allocation ?? true,
    inventory: options?.locks?.inventory ?? true,
  };

  const {
    loading,
    product_variations,
    products_variations_paging,
    products_variations_filters_chip,
    products_variations_filters,
  } = useSelector((state) => state.products_variations);

  const loadProductVariations = () => {
    const filtros =
      products_variations_filters_chip.length === 0 ?
        { name: '', smart_filter: true }
      : products_variations_filters_chip.reduce(
          (a, v) => ({
            ...a,
            [`${_.lowerCase(v[3])}[${getRequestOperator(v[1], 0)}]`]: v[2],
          }),
          { smart_filter: true }
        );

    dispatch(CreatorsVariations.productVariationLoading(true));
    CreatorsVariations.getProdutctVariationsAll({
      limit: products_variations_paging.limit,
      offset: products_variations_paging.offset * products_variations_paging.limit,
      ...filtros,
    })
      .then(({ data, paging, available_filters }) => {
        dispatch([
          CreatorsVariations.productVariationLoading(false),
          CreatorsVariations.productSetVariations(data),
          CreatorsVariations.productsVariationsSetFilters(available_filters),
          CreatorsVariations.productVariationPaging({
            ...paging,
            offset: parseInt(paging.offset / paging.limit),
          }),
        ]);

        tableRef.current &&
          tableRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
      })
      .catch((error) => {
        dispatch(CreatorsVariations.productVariationLoading(false));
        enqueueSnackbar(`${getErrors(error)}`, { variant: 'error' });
      });
  };

  useEffect(() => {
    loadProductVariations();
  }, [products_variations_paging.offset, products_variations_paging.limit, products_variations_filters_chip]);

  useEffect(() => {
    setSelp([]);
    tableRef.current = null;
  }, [onClose]);

  const handleChangePage = (newPage) =>
    dispatch(
      CreatorsVariations.productVariationPaging({
        ...products_variations_paging,
        offset: newPage,
      })
    );

  const handleChangeRowsPerPage = (newLimit) =>
    dispatch(
      CreatorsVariations.productVariationPaging({
        ...products_variations_paging,
        limit: newLimit,
        offset: 0,
      })
    );

  return (
    <Modal
      open={open}
      width={'80dvw'}
      height={'80dvh'}>
      <Loading loading={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            marginBottom: 1,
            minHeight: 0,
            gap: 1,
          }}>
          <UnicFilterBar
            defaultFilter={products_variations_filters.filter((item) => item.is_default === true)[0]}
            filtersValues={products_variations_filters_chip}
            onFiltersValues={(value) => {
              dispatch([
                CreatorsVariations.productsVariationsSetFiltersChip(value),
                CreatorsVariations.productVariationPaging({
                  limit: products_variations_paging.limit,
                  total: 0,
                  offset: 0,
                }),
              ]);
            }}
            filterOptions={products_variations_filters}
            placeholder='Digite o nome do produto e aperte enter'
            drawPaper={false}
          />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Button
              onClick={() => onClose(null)}
              sx={{ height: '46px', marginTop: '8px' }}
              disabled={loading}>
              <FlatIcon icon={FlatIconOptions.close} /> Fechar
            </Button>
            <Button
              onClick={() => {
                const products = (product_variations || []).filter((_, productIndex) => selp.includes(productIndex));

                onClose(products);
              }}
              variant='contained'
              sx={{ height: '46px', marginTop: '8px' }}
              disabled={loading}>
              <FlatIcon icon={FlatIconOptions.new} />
              Adicionar
            </Button>
          </Box>
        </Box>
        <DataGrid
          tableRef={tableRef}
          isLoading={loading}
          columns={[
            {
              actions: (data, rowIndex) => {
                const noPositions =
                  (data.stock?.stock_locations?.map((v) => v.positions.length).reduce((a, b) => a + b, 0) ?? 0) <= 0;

                const hasPendingInventory = data.stock?.stock_locations?.some((location) =>
                  location?.positions?.some(
                    (position) =>
                      position?.inventory?.inventory_status === 1 &&
                      position?.inventory?.inventory_type === 'P' &&
                      position?.inventory?.idf_auto_added === 'N'
                  )
                );

                if (hasPendingInventory && locks?.inventory) {
                  return (
                    <Tooltip title='Existem inventários em andamento para o produto.'>
                      <Lock color='disabled' />
                    </Tooltip>
                  );
                }

                if (noPositions && locks?.allocation) {
                  return (
                    <Tooltip title='Produto sem posições alocadas.'>
                      <AppsOutage color='disabled' />
                    </Tooltip>
                  );
                }

                return (
                  <Checkbox
                    key={rowIndex}
                    checked={selp.includes(rowIndex)}
                    onChange={(chck) => {
                      setSelp((prev) => {
                        if (chck.target.checked) {
                          return [...prev, rowIndex];
                        }

                        return prev.filter((i) => i !== rowIndex);
                      });
                    }}
                  />
                );
              },
              headerStyle: { width: '30px' },
              cellStyle: { width: '30px' },
            },
            {
              title: 'Produto',
              render: (rowData) => (
                <Image
                  style={{
                    height: 50,
                    width: 50,
                    border: '1px solid #ddd',
                    borderRadius: '10%',
                    objectFit: 'contain',
                  }}
                  src={rowData?.main_thumb_secure_url}
                />
              ),
              headerStyle: { margin: 0, padding: 0 },
              cellStyle: { margin: 0, padding: 1 },
            },
            {
              title: 'Nome do produto',
              render: (data) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                  <Typography>{data?.owner_product_name}</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                    }}>
                    {data?.attributes?.map((item, index) => (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 0.5,
                        }}>
                        <Typography
                          variant='caption'
                          sx={{
                            opacity: 0.8,
                          }}>
                          <strong>{_.capitalize(item.name)}:</strong>
                        </Typography>
                        <Typography
                          variant='caption'
                          sx={{
                            opacity: 0.8,
                          }}>
                          {data.attributes[index].value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ),
              headerStyle: { textAlign: 'left' },
              cellStyle: { textAlign: 'left' },
            },
            {
              // Por falta de campos retornados no horse
              title: 'Tipo',
              render: (data) => <Typography sx={{ fontSize: '14px' }}>{data?.id ? 'Variado' : 'Simples'}</Typography>,
              headerStyle: { textAlign: 'left' },
              cellStyle: { textAlign: 'left' },
            },
            {
              title: 'GTIN (EAN)',
              field: 'gtin',
              headerStyle: { textAlign: 'left' },
              cellStyle: { textAlign: 'left' },
            },
            {
              title: 'Marca',
              field: 'owner_product_brand',
              headerStyle: { textAlign: 'left' },
              cellStyle: { textAlign: 'left' },
            },
            {
              title: 'Modelo',
              field: 'owner_product_model',
              headerStyle: { textAlign: 'left' },
              cellStyle: { textAlign: 'left' },
            },
            {
              title: 'Referência',
              field: 'reference',
              headerStyle: { textAlign: 'left' },
              cellStyle: { textAlign: 'left' },
            },
            {
              title: (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='h6'>Estoque</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', gap: 1 }}>
                    <Typography
                      variant='h6'
                      sx={{ marginLeft: 1, fontWeight: 'bold' }}>
                      ERP
                    </Typography>
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 'bold' }}>
                      {' '}
                      |{' '}
                    </Typography>
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 'bold' }}>
                      WMS
                    </Typography>
                  </Box>
                </Box>
              ),
              render: (data) => (
                <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', gap: 1 }}>
                  <Tooltip
                    title={
                      <ul>
                        <li>Estoque físico: {data.stock?.stock_erp?.physical_quantity}</li>
                        <li>Estoque reservado: {data.stock?.stock_erp?.reserved_quantity}</li>
                        <li>Estoque disponível: {data.stock?.stock_erp?.available_quantity}</li>
                      </ul>
                    }
                    aria-label='add'>
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 'bold',
                        color: 'gray',
                      }}>
                      {data?.stock?.stock_erp?.physical_quantity}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 'bold' }}>
                    {' '}
                    |{' '}
                  </Typography>
                  <Typography
                    variant='h4'
                    sx={{ fontWeight: 'bold' }}>
                    {data?.stock?.physical_quantity}
                  </Typography>
                </Box>
              ),
              headerStyle: { textAlign: 'center' },
              cellStyle: { textAlign: 'center' },
            },
          ]}
          data={product_variations}
          options={{
            rowStyle: { fontSize: 14 },
            maxBodyHeight: getScreenSize().height - 375,
            minBodyHeight: getScreenSize().height - 375,
            sorting: false,
            selection: true,
            selectionProps: (rowData) => ({
              disabled: simpleAddress(rowData.stock)?.length > 0 ? false : true,
              color: 'primary',
            }),
          }}
        />
        <DataGridPagination
          count={products_variations_paging?.total}
          page={products_variations_paging?.offset}
          rowsPerPage={products_variations_paging?.limit}
          handleChangePage={(newPage) => handleChangePage(newPage)}
          handleChangeRowsPerPage={(newLimit) => handleChangeRowsPerPage(newLimit)}
        />
      </Box>
    </Modal>
  );
}
